import { OperationType, SafeTransaction } from '@polymarket/builder-relayer-client';
import { createPublicClient, encodeFunctionData, erc20Abi, http } from 'viem';
import { polygon } from 'viem/chains';

export const POLYGON_RPC_URL = 'https://polygon-bor-rpc.publicnode.com';

import {
  COLLATERAL_OFFRAMP_ADDRESS,
  COLLATERAL_ONRAMP_ADDRESS,
  CTF_COLLATERAL_ADAPTER_ADDRESS,
  CTF_CONTRACT_ADDRESS,
  CTF_EXCHANGE_ADDRESS,
  NEG_RISK_ADAPTER_ADDRESS,
  NEG_RISK_CTF_COLLATERAL_ADAPTER_ADDRESS,
  NEG_RISK_CTF_EXCHANGE_ADDRESS,
  PUSD_CONTRACT_ADDRESS,
  USDC_E_CONTRACT_ADDRESS,
} from '@/constants/tokens.constants';

const MAX_UINT256 =
  '115792089237316195423570985008687907853269984665640564039457584007913129639935';

const erc1155Abi = [
  {
    inputs: [
      { name: 'operator', type: 'address' },
      { name: 'approved', type: 'bool' },
    ],
    name: 'setApprovalForAll',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [
      { name: 'account', type: 'address' },
      { name: 'operator', type: 'address' },
    ],
    name: 'isApprovedForAll',
    outputs: [{ name: '', type: 'bool' }],
    stateMutability: 'view',
    type: 'function',
  },
] as const;

export const publicClient = createPublicClient({
  chain: polygon,
  transport: http(POLYGON_RPC_URL as string), // Uses default public RPCs (more reliable than hardcoded public Ankr)
});

//

// Spenders that need pUSD approval (V2 exchange addresses via updated constants)
const PUSD_SPENDERS = [
  { address: CTF_CONTRACT_ADDRESS, name: 'CTF Contract' },
  { address: NEG_RISK_ADAPTER_ADDRESS, name: 'Neg Risk Adapter' },
  { address: CTF_EXCHANGE_ADDRESS, name: 'CTF Exchange V2' },
  { address: NEG_RISK_CTF_EXCHANGE_ADDRESS, name: 'Neg Risk CTF Exchange V2' },
  { address: COLLATERAL_OFFRAMP_ADDRESS, name: 'Collateral Offramp' },
  { address: CTF_COLLATERAL_ADAPTER_ADDRESS, name: 'CTF Collateral Adapter' },
  { address: NEG_RISK_CTF_COLLATERAL_ADAPTER_ADDRESS, name: 'Neg Risk CTF Collateral Adapter' },
] as const;

// Spenders that need USDC.e approval (specifically the migration onramp for V2)
const USDC_E_SPENDERS = [
  { address: COLLATERAL_ONRAMP_ADDRESS, name: 'Collateral Bridge (Migration)' },
] as const;

const OUTCOME_TOKEN_SPENDERS = [
  { address: CTF_EXCHANGE_ADDRESS, name: 'CTF Exchange' },
  { address: NEG_RISK_CTF_EXCHANGE_ADDRESS, name: 'Neg Risk Exchange' },
  { address: NEG_RISK_ADAPTER_ADDRESS, name: 'Neg Risk Adapter' },
  { address: CTF_COLLATERAL_ADAPTER_ADDRESS, name: 'CTF Collateral Adapter' },
  { address: NEG_RISK_CTF_COLLATERAL_ADAPTER_ADDRESS, name: 'Neg Risk CTF Collateral Adapter' },
] as const;

const checkAllowanceForToken = async (
  tokenAddress: string,
  safeAddress: string,
  spender: string
): Promise<boolean> => {
  try {
    const allowance = await publicClient.readContract({
      address: tokenAddress as `0x${string}`,
      abi: erc20Abi,
      functionName: 'allowance',
      args: [safeAddress as `0x${string}`, spender as `0x${string}`],
      authorizationList: undefined,
    });

    const threshold = BigInt('1000000000000');
    return allowance >= threshold;
  } catch (error) {
    console.error(
      `[Approvals] Failed to check allowance for token ${tokenAddress} / spender ${spender}:`,
      error
    );
    return false;
  }
};

const checkERC1155ApprovalForSpender = async (
  safeAddress: string,
  spender: string
): Promise<boolean> => {
  try {
    const isApproved = await publicClient.readContract({
      address: CTF_CONTRACT_ADDRESS as `0x${string}`,
      abi: erc1155Abi,
      functionName: 'isApprovedForAll',
      args: [safeAddress as `0x${string}`, spender as `0x${string}`],
      authorizationList: undefined,
    });

    return isApproved;
  } catch (error) {
    console.error(`[Approvals] Failed to check ERC1155 status for ${spender}:`, error);
    return false;
  }
};

export const checkAllApprovals = async (
  safeAddress: string
): Promise<{
  allApproved: boolean;
  usdcApprovals: Record<string, boolean>;
  outcomeTokenApprovals: Record<string, boolean>;
}> => {
  const usdcApprovals: Record<string, boolean> = {}; // UI still calls it usdcApprovals for compatibility
  const outcomeTokenApprovals: Record<string, boolean> = {};

  await Promise.all(
    PUSD_SPENDERS.map(async ({ address, name }) => {
      usdcApprovals[name] = await checkAllowanceForToken(
        PUSD_CONTRACT_ADDRESS,
        safeAddress,
        address
      );
    })
  );

  await Promise.all(
    USDC_E_SPENDERS.map(async ({ address, name }) => {
      // Logic for UI compatibility: we just add them to the same pool for "allApproved"
      usdcApprovals[name] = await checkAllowanceForToken(
        USDC_E_CONTRACT_ADDRESS,
        safeAddress,
        address
      );
    })
  );

  await Promise.all(
    OUTCOME_TOKEN_SPENDERS.map(async ({ address, name }) => {
      outcomeTokenApprovals[name] = await checkERC1155ApprovalForSpender(safeAddress, address);
    })
  );

  const allApproved =
    Object.values(usdcApprovals).every((approved) => approved) &&
    Object.values(outcomeTokenApprovals).every((approved) => approved);

  return {
    allApproved,
    usdcApprovals,
    outcomeTokenApprovals,
  };
};

export const createAllApprovalTxs = (): SafeTransaction[] => {
  const safeTxns: SafeTransaction[] = [];

  // V2: Approve pUSD (new collateral) instead of USDC.e to the exchange contracts
  for (const { address } of PUSD_SPENDERS) {
    safeTxns.push({
      to: PUSD_CONTRACT_ADDRESS,
      operation: OperationType.Call,
      data: encodeFunctionData({
        abi: erc20Abi,
        functionName: 'approve',
        args: [address as `0x${string}`, BigInt(MAX_UINT256)],
      }),
      value: '0',
    });
  }

  // Migration: Approve USDC.e to the Collateral Onramp
  for (const { address } of USDC_E_SPENDERS) {
    safeTxns.push({
      to: USDC_E_CONTRACT_ADDRESS,
      operation: OperationType.Call,
      data: encodeFunctionData({
        abi: erc20Abi,
        functionName: 'approve',
        args: [address as `0x${string}`, BigInt(MAX_UINT256)],
      }),
      value: '0',
    });
  }

  for (const { address } of OUTCOME_TOKEN_SPENDERS) {
    safeTxns.push({
      to: CTF_CONTRACT_ADDRESS,
      operation: OperationType.Call,
      data: encodeFunctionData({
        abi: erc1155Abi,
        functionName: 'setApprovalForAll',
        args: [address as `0x${string}`, true],
      }),
      value: '0',
    });
  }

  return safeTxns;
};
